import React, { useState, useEffect } from 'react';
import {
  Users, BookOpen, GraduationCap, CalendarCheck,
  Award, ClipboardList, Brain, TrendingUp,
  ChevronRight, CheckCircle, AlertCircle, Sparkles,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Area, AreaChart,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../api';

/* ── Dark tooltip for charts ─────────────────────────────── */
const DarkTooltip = ({ active, payload, label, suffix = '' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="px-3 py-2 rounded-xl text-sm shadow-xl"
      style={{ backgroundColor: '#0F172A', border: 'none' }}
    >
      <p className="text-slate-400 text-xs mb-0.5">{label}</p>
      <p className="font-semibold text-white">{payload[0]?.value}{suffix}</p>
    </div>
  );
};

/* ── Stat card ───────────────────────────────────────────── */
function StatCard({ label, value, sub, icon: Icon, accentColor, testId }) {
  return (
    <div
      className="bg-white rounded-2xl p-6 group cursor-default"
      style={{
        border: '1px solid #E2E8F0',
        transition: 'box-shadow 0.3s ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.06)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <p
          className="text-[10px] font-bold uppercase"
          style={{ letterSpacing: '0.18em', color: '#94A3B8' }}
        >
          {label}
        </p>
        <div
          className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${accentColor}12` }}
        >
          <Icon className="w-4 h-4" strokeWidth={1.75} style={{ color: accentColor }} />
        </div>
      </div>
      <p
        className="mt-3 font-light tracking-tighter leading-none"
        style={{ fontSize: '2.25rem', color: '#0F172A', fontFamily: 'Manrope, sans-serif' }}
      >
        {value}
      </p>
      <p className="mt-1.5 text-xs" style={{ color: '#94A3B8' }}>{sub}</p>
    </div>
  );
}

/* ── Quick action card ───────────────────────────────────── */
function QuickAction({ label, desc, icon: Icon, accentColor, path, navigate, testId }) {
  return (
    <button
      onClick={() => navigate(path)}
      data-testid={testId}
      className="text-left group w-full"
      style={{
        backgroundColor: '#F8FAFC',
        border: '1px solid #E2E8F0',
        borderRadius: '14px',
        padding: '18px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'background-color 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.backgroundColor = '#FFFFFF';
        e.currentTarget.style.borderColor = `${accentColor}40`;
        e.currentTarget.style.boxShadow = `0 8px 24px ${accentColor}0F`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.backgroundColor = '#F8FAFC';
        e.currentTarget.style.borderColor = '#E2E8F0';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div className="flex items-center gap-4">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${accentColor}14` }}
        >
          <Icon className="w-5 h-5" strokeWidth={1.75} style={{ color: accentColor }} />
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-800">{label}</p>
          <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>{desc}</p>
        </div>
      </div>
      <ChevronRight
        className="w-4 h-4 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-0.5"
        style={{ color: '#CBD5E1' }}
        strokeWidth={1.75}
      />
    </button>
  );
}

/* ── Main Dashboard ──────────────────────────────────────── */
export default function TeacherDashboard() {
  const { user, school } = useAuth();
  const navigate = useNavigate();
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/teachers/me/dashboard-stats')
      .then(r => { if (r.success) setDashData(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const firstName = user?.first_name || 'Teacher';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const dateStr = new Date().toLocaleDateString('en-NG', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const classCount   = dashData?.class_count ?? 0;
  const studentCount = dashData?.student_count ?? 0;
  const subjectCount = dashData?.assigned_count ?? 0;
  const todayAttEntry = dashData?.attendance_trend?.slice(-1)[0];
  const latestRate   = todayAttEntry?.rate;
  const todayPresent = todayAttEntry?.present ?? 0;
  const todayTotal   = todayAttEntry?.total ?? 0;
  const myClass      = dashData?.my_class;
  const classPerf    = dashData?.class_performance || [];
  const attTrend     = dashData?.attendance_trend || [];
  const recentAsn    = dashData?.recent_assignments || [];

  const QUICK_ACTIONS = [
    { label: 'My Class',       desc: 'Attendance & register',  icon: CalendarCheck, accentColor: '#10B981', path: '/teacher/my-class',    testId: 'quick-my-class' },
    { label: 'Enter Results',  desc: 'Record student scores',  icon: Award,         accentColor: '#F59E0B', path: '/teacher/results',      testId: 'quick-enter-results' },
    { label: 'Assignments',    desc: 'Create & grade work',    icon: ClipboardList, accentColor: '#8B5CF6', path: '/teacher/assignments',  testId: 'quick-assignments' },
    { label: 'My Students',    desc: 'View your student list', icon: GraduationCap, accentColor: '#2563EB', path: '/teacher/my-students',  testId: 'quick-my-students' },
    { label: 'Lesson Notes',   desc: 'Write & share notes',    icon: BookOpen,      accentColor: '#06B6D4', path: '/teacher/lessons',      testId: 'quick-lesson-notes' },
    { label: 'AI Assistant',   desc: 'Generate lesson plans',  icon: Brain,         accentColor: '#EC4899', path: '/teacher/ai',           testId: 'quick-ai-assistant' },
  ];

  return (
    <div className="p-6 lg:p-8 space-y-7" data-testid="teacher-dashboard">

      {/* ── HERO CARD (dark luxury) ──────────────────────────── */}
      <div
        className="rounded-3xl p-8 lg:p-10 relative overflow-hidden"
        style={{
          backgroundColor: '#0F172A',
          boxShadow: '0 25px 60px rgba(0,0,0,0.18)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}
        data-testid="dashboard-hero"
      >
        {/* Geometric texture overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url("https://images.unsplash.com/photo-1454117096348-e4abbeba002c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGNsZWFuJTIwc3VidGxlJTIwZ2VvbWV0cmljJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODcwNTM3MjV8MA&ixlib=rb-4.1.0&q=85")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.06,
            mixBlendMode: 'overlay',
          }}
        />
        {/* Radial glow */}
        <div
          className="absolute -top-20 -right-20 w-64 h-64 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(37,99,235,0.20) 0%, transparent 70%)',
          }}
        />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          {/* Left: greeting */}
          <div>
            <p
              className="text-[11px] font-bold uppercase mb-2"
              style={{ color: '#475569', letterSpacing: '0.18em' }}
            >
              {school?.name || 'School'} · {dateStr}
            </p>
            <h1
              className="text-3xl lg:text-4xl font-extrabold text-white tracking-tight leading-tight"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {greeting}, {firstName}
            </h1>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              {myClass ? (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase"
                  style={{
                    letterSpacing: '0.1em',
                    backgroundColor: 'rgba(59,130,246,0.15)',
                    color: '#93C5FD',
                    border: '1px solid rgba(59,130,246,0.25)',
                  }}
                >
                  <Sparkles className="w-3 h-3" strokeWidth={2} />
                  Class Teacher · {myClass.name}
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase"
                  style={{
                    letterSpacing: '0.1em',
                    backgroundColor: 'rgba(16,185,129,0.12)',
                    color: '#6EE7B7',
                    border: '1px solid rgba(16,185,129,0.2)',
                  }}
                >
                  Subject Teacher
                </span>
              )}
            </div>
          </div>

          {/* Right: inline key metrics */}
          {!loading && (
            <div className="flex gap-4 flex-shrink-0 flex-wrap">
              <div
                className="rounded-2xl px-5 py-3"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <p
                  className="text-2xl font-light tracking-tighter text-white"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {loading ? '—' : classCount}
                </p>
                <p
                  className="text-[10px] font-bold uppercase mt-0.5"
                  style={{ color: '#64748B', letterSpacing: '0.15em' }}
                >
                  Classes
                </p>
              </div>
              <div
                className="rounded-2xl px-5 py-3"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <p
                  className="text-2xl font-light tracking-tighter text-white"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {loading ? '—' : studentCount}
                </p>
                <p
                  className="text-[10px] font-bold uppercase mt-0.5"
                  style={{ color: '#64748B', letterSpacing: '0.15em' }}
                >
                  Students
                </p>
              </div>
              {latestRate != null && (
                <div
                  className="rounded-2xl px-5 py-3"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  {todayTotal > 0 ? (
                    <>
                      <p
                        className="text-2xl font-light tracking-tighter"
                        style={{ fontFamily: 'Manrope, sans-serif', color: latestRate >= 75 ? '#34D399' : '#FCA5A5' }}
                      >
                        {latestRate}%
                      </p>
                      <p
                        className="text-[10px] font-bold uppercase mt-0.5"
                        style={{ color: '#64748B', letterSpacing: '0.15em' }}
                      >
                        {todayPresent}/{todayTotal} Today
                      </p>
                    </>
                  ) : (
                    <>
                      <p
                        className="text-sm font-semibold"
                        style={{ color: '#F59E0B' }}
                      >
                        Not Marked
                      </p>
                      <p
                        className="text-[10px] font-bold uppercase mt-0.5"
                        style={{ color: '#64748B', letterSpacing: '0.15em' }}
                      >
                        Attendance Today
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── STAT CARDS ────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-white rounded-2xl animate-pulse" style={{ border: '1px solid #E2E8F0' }} />
          ))
        ) : (
          <>
            <StatCard label="My Classes"  value={classCount}   sub="Class assignments"    icon={GraduationCap} accentColor="#2563EB" testId="stat-my-classes" />
            <StatCard label="Students"    value={studentCount} sub="Across my classes"    icon={Users}         accentColor="#10B981" testId="stat-students" />
            <StatCard label="Subjects"    value={subjectCount} sub="Subject assignments"  icon={BookOpen}      accentColor="#F59E0B" testId="stat-subjects" />
            <StatCard
              label="Attendance"
              value={latestRate != null ? `${latestRate}%` : 'N/A'}
              sub={
                myClass
                  ? todayTotal > 0
                    ? `${todayPresent} of ${todayTotal} present today`
                    : 'Not marked today — mark now'
                  : 'Class teachers only'
              }
              icon={CalendarCheck}
              accentColor="#8B5CF6"
              testId="stat-attendance"
            />
          </>
        )}
      </div>

      {/* ── QUICK ACTIONS ─────────────────────────────────────── */}
      <div>
        <p
          className="text-[11px] font-bold uppercase mb-4"
          style={{ color: '#94A3B8', letterSpacing: '0.18em' }}
        >
          Quick Actions
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {QUICK_ACTIONS.map((a) => (
            <QuickAction key={a.path} {...a} navigate={navigate} />
          ))}
        </div>
      </div>

      {/* ── CHARTS ────────────────────────────────────────────── */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Class Performance */}
        <div
          className="bg-white rounded-2xl overflow-hidden"
          style={{ border: '1px solid #E2E8F0' }}
          data-testid="class-performance-chart"
        >
          <div className="px-6 py-4" style={{ borderBottom: '1px solid #F1F5F9' }}>
            <p className="font-semibold text-slate-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Class Performance</p>
            <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>Average score across your classes</p>
          </div>
          {loading ? (
            <div className="p-6 space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-8 bg-slate-50 rounded-lg animate-pulse" />)}</div>
          ) : classPerf.length === 0 ? (
            <div className="py-14 text-center">
              <TrendingUp className="w-7 h-7 mx-auto mb-2" style={{ color: '#E2E8F0' }} strokeWidth={1.5} />
              <p className="text-sm" style={{ color: '#94A3B8' }}>No performance data yet</p>
              <p className="text-xs mt-1" style={{ color: '#CBD5E1' }}>Enter student results to see class averages</p>
            </div>
          ) : (
            <div className="p-5 pb-3">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={classPerf} barSize={24} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                  <XAxis dataKey="class_name" tick={{ fontSize: 11, fill: '#94A3B8', fontFamily: 'Figtree' }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#94A3B8', fontFamily: 'Figtree' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<DarkTooltip suffix="%" />} cursor={{ fill: 'rgba(37,99,235,0.04)' }} />
                  <Bar dataKey="avg" fill="#2563EB" radius={[5, 5, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* 7-Day Attendance Trend */}
        <div
          className="bg-white rounded-2xl overflow-hidden"
          style={{ border: '1px solid #E2E8F0' }}
          data-testid="attendance-trend-chart"
        >
          <div className="px-6 py-4" style={{ borderBottom: '1px solid #F1F5F9' }}>
            <p className="font-semibold text-slate-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>7-Day Attendance Trend</p>
            <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>
              {myClass ? `${myClass.name} — your class` : 'Available for class teachers only'}
            </p>
          </div>
          {loading ? (
            <div className="p-6 space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-8 bg-slate-50 rounded-lg animate-pulse" />)}</div>
          ) : attTrend.length === 0 ? (
            <div className="py-14 text-center">
              <CalendarCheck className="w-7 h-7 mx-auto mb-2" style={{ color: '#E2E8F0' }} strokeWidth={1.5} />
              <p className="text-sm" style={{ color: '#94A3B8' }}>No attendance data yet</p>
              <p className="text-xs mt-1" style={{ color: '#CBD5E1' }}>
                {myClass ? 'Mark attendance from My Class page' : 'Assign as class teacher to see trends'}
              </p>
            </div>
          ) : (
            <div className="p-5 pb-3">
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={attTrend} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                  <defs>
                    <linearGradient id="attGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#10B981" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94A3B8', fontFamily: 'Figtree' }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#94A3B8', fontFamily: 'Figtree' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<DarkTooltip suffix="%" />} />
                  <Area type="monotone" dataKey="rate" stroke="#10B981" strokeWidth={2.5} fill="url(#attGradient)" dot={{ fill: '#10B981', r: 3, strokeWidth: 0 }} activeDot={{ r: 5, strokeWidth: 0 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* ── RECENT ASSIGNMENTS ────────────────────────────────── */}
      <div
        className="bg-white rounded-2xl overflow-hidden"
        style={{ border: '1px solid #E2E8F0' }}
        data-testid="recent-assignments"
      >
        <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #F1F5F9' }}>
          <p className="font-semibold text-slate-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Recent Assignments</p>
          <button
            onClick={() => navigate('/teacher/assignments')}
            className="flex items-center gap-1 text-xs font-medium transition-colors"
            style={{ color: '#2563EB' }}
            data-testid="view-all-assignments-btn"
            onMouseEnter={e => e.currentTarget.style.color = '#1D4ED8'}
            onMouseLeave={e => e.currentTarget.style.color = '#2563EB'}
          >
            View all <ChevronRight className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </div>

        {loading ? (
          <div className="p-6 space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-14 bg-slate-50 rounded-xl animate-pulse" />)}</div>
        ) : recentAsn.length === 0 ? (
          <div className="py-14 text-center">
            <ClipboardList className="w-7 h-7 mx-auto mb-2" style={{ color: '#E2E8F0' }} strokeWidth={1.5} />
            <p className="text-sm" style={{ color: '#94A3B8' }}>No assignments created yet</p>
            <button
              onClick={() => navigate('/teacher/assignments')}
              className="mt-3 px-4 py-2 rounded-xl text-xs font-semibold text-white"
              style={{ backgroundColor: '#2563EB' }}
              data-testid="create-assignment-shortcut-btn"
            >
              Create Assignment
            </button>
          </div>
        ) : (
          <div>
            {recentAsn.map((asn, i) => {
              const isOverdue = asn.due_date && new Date(asn.due_date) < new Date();
              return (
                <div
                  key={asn.id || i}
                  className="px-6 py-4 flex items-center justify-between group"
                  style={{
                    borderBottom: i < recentAsn.length - 1 ? '1px solid #F8FAFC' : 'none',
                    transition: 'transform 0.2s ease, background-color 0.2s ease',
                    cursor: 'default',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(3px)'; e.currentTarget.style.backgroundColor = '#FAFBFC'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateX(0)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                  data-testid={`assignment-row-${asn.id || i}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: isOverdue ? '#FEE2E2' : '#EFF6FF' }}
                    >
                      <ClipboardList
                        className="w-4 h-4"
                        strokeWidth={1.75}
                        style={{ color: isOverdue ? '#EF4444' : '#2563EB' }}
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800 leading-tight">{asn.title}</p>
                      <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>
                        {asn.subject_name} · {(asn.class_names || []).join(', ')}
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-4">
                    <div className="flex items-center gap-1 justify-end">
                      <CheckCircle className="w-3 h-3 text-emerald-500" strokeWidth={2} />
                      <span className="text-xs font-semibold text-slate-600">{asn.submission_count} submitted</span>
                    </div>
                    {asn.due_date && (
                      <p
                        className="text-[11px] mt-0.5 font-medium"
                        style={{ color: isOverdue ? '#EF4444' : '#94A3B8' }}
                      >
                        Due {new Date(asn.due_date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short' })}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
